import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Auth0Metadata } from '../../api/API';
import { Notification } from '../../api/models/Notification';
import { Environment } from '../../Environment';
import { notificationSlice } from '../../model/notificationSlice';
import { RootState } from '../../model/store';
import { useToken } from '../../util/GetTokenHook';
import { Auth0Context, useAuth0 } from '../Auth0Provider';
import { Role } from '../PrivateRoute';

export type NotificationsScreenProps = {
  notifications: Notification[];
  isGettingNotifications: boolean;
};

export const notificationsContainer = (Screen: React.ComponentType<NotificationsScreenProps>) => () => {
  const notifications = useSelector((state: RootState) => state.notification.list);
  const dispatch = useDispatch();

  const [isGettingNotifications, setIsGettingNotifications] = React.useState(false);

  const { api } = Environment.current();
  const { user } = useAuth0() as Auth0Context;

  const personId = user?.[Auth0Metadata.PersonId];
  const roles = user?.[Auth0Metadata.Roles]?.length ? user[Auth0Metadata.Roles] : [Role.Student];
  const isAdmin = Boolean([Role.Admin].find(allowedRole => roles.includes(allowedRole)));

  // Custom hooks
  const { token, isGettingToken } = useToken();

  // To prevent updates on unmount
  const isMounted = useRef(true);
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (token) {
      (async () => {
        try {
          setIsGettingNotifications(true);

          let list: Notification[] = [];
          if (!isAdmin && personId) {
            // student
            list = await api.getStudentNotifications(personId, token);
          } else {
            // admin
            list = await api.getNotifications(token);
          }

          if (isMounted.current) {
            setIsGettingNotifications(false);
            dispatch(notificationSlice.actions.receivedList({ list }));
          }
        } catch (e) {
          toast.error(e.message);
          setIsGettingNotifications(false);
        }
      })();
    }
  }, [token, isAdmin, personId]);

  return <Screen isGettingNotifications={isGettingNotifications || isGettingToken} notifications={notifications} />;
};
